// error tacking
import { sizePacksFetchComplete } from "actions/collections/collection-size-packs";
import { add as snackbarAdd } from "actions/snackbars";
import { SaveAssormentSettingsRequest } from "components/product-collection/market-settings/market-settings.types";
import { COLLECTIONS } from "constants/action-types";

import { dateFormat } from "helpers/dates";
import { formatMessage } from "lib/globalize-util";
import { ajax } from "lib/observable";
import pick from "lodash/pick";
import moment from "moment";

import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import siteConfig from "site-config";

export const updateCollectionSettingsEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.START)
        .debounce(() => Observable.timer(500))
        .map(({ payload }) => payload)
        .flatMap(
            ({
                assortmentName,
                departmentId,
                marketDate,
                dueDate,
                brandContactEmail,
                brandContactName,
                marketStatus,
                rowCount,
                smuCount,
                totalCost = "0",
                totalRetailValue = "0",
                totalUnits,
                quantityRowsWithUnits,
                totalSized,
                exportCount,
                onSettled,
                departmentName,
                departmentAndNumbers,
                brandName,
            }: SaveAssormentSettingsRequest) => {
                const { collectionItemMerchantId, collectionItemId, merchantId } = store.getState().collection;
                const userItemMerchandId =
                    merchantId && collectionItemMerchantId !== merchantId ? merchantId : collectionItemMerchantId;
                const isOnline = navigator.onLine === true;

                return isOnline
                    ? ajax({
                          body: {
                              name: assortmentName,
                              collection_facet: {
                                  department_id: departmentId,
                                  brand_contact_email: brandContactEmail,
                                  brand_contact_name: brandContactName,
                                  due_date: dueDate ? moment(dueDate).format(dateFormat.YEAR_MONTH_DAY) : "",
                                  market_date: marketDate ? moment(marketDate).format(dateFormat.YEAR_MONTH_DAY) : "",
                                  market_status: marketStatus,
                                  row_count: rowCount,
                                  smu_count: smuCount,
                                  total_cost: totalCost.toString(),
                                  total_retail_value: totalRetailValue.toString(),
                                  total_units: totalUnits,
                                  row_with_units_count: quantityRowsWithUnits,
                                  total_sized: totalSized,
                                  export_count: exportCount,
                                  department_name: departmentName,
                                  department_from_rows: departmentAndNumbers,
                                  brand_name: brandName,
                              },
                              field_mask: {
                                  paths: [
                                      "name",
                                      "collection_facet.department_id",
                                      "collection_facet.brand_contact_email",
                                      "collection_facet.brand_contact_name",
                                      "collection_facet.due_date",
                                      "collection_facet.market_date",
                                      "collection_facet.market_status",
                                      "collection_facet.row_count",
                                      "collection_facet.smu_count",
                                      "collection_facet.total_cost",
                                      "collection_facet.total_retail_value",
                                      "collection_facet.total_units",
                                      "collection_facet.row_with_units_count",
                                      "collection_facet.total_sized",
                                      "collection_facet.export_count",
                                      "collection_facet.department_name",
                                      "collection_facet.department_from_rows",
                                      "collection_facet.brand_name",
                                  ],
                              },
                          },
                          method: "PUT",
                          url: `${siteConfig.goAPI}/v1/merchants/${userItemMerchandId}/space/items/${collectionItemId}`,
                      })
                          .mergeMap((res) => {
                              setTimeout(() => onSettled?.(), 100);
                              return Observable.of({
                                  payload: {
                                      name: res.response.name,
                                      collection_facet: pick(res.response.collection_facet, [
                                          "department_id",
                                          "brand_contact_email",
                                          "brand_contact_name",
                                          "due_date",
                                          "market_date",
                                          "market_status",
                                          "row_count",
                                          "smu_count",
                                          "total_cost",
                                          "total_retail_value",
                                          "total_units",
                                          "row_with_units_count",
                                          "total_sized",
                                          "export_count",
                                          "department_name",
                                          "department_from_rows",
                                          "brand_name",
                                      ]),
                                  },
                                  type: COLLECTIONS.COLLECTION.UPDATE_COLLECTION_SETTINGS.COMPLETED,
                              });
                          })
                          .catch((e) => {
                              const errorMessage = e?.xhr?.response?.error || e.message;

                              onSettled?.(errorMessage);

                              return Observable.of(snackbarAdd({ message: errorMessage }));
                          })
                    : [];
            },
        )
        .catch(() => {
            return Observable.merge(
                Observable.of(snackbarAdd({ message: formatMessage("There was an error updating your assortment") })),
            );
        });
};

const SIZE_PACKS_LIMIT = 1000;
const SIZE_PACKS_LIMIT_WITH_PAGINATION = 100;
const fullyFetchedBrands = [];
export const getSizePacksForBrand = (action$, store$) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.SIZE_PACKS.FETCH.START)
        .map(({ payload }) => payload)
        .mergeMap(({ brand, includeNormalized, lastFetchedId }) => {
            const { collection } = store$.getState();
            const { sizePacks } = collection;

            if (fullyFetchedBrands.includes(brand) || (sizePacks[brand] && !lastFetchedId)) {
                return [sizePacksFetchComplete()];
            }

            const queryString = `__brand=${brand}&includeInactive=true&__limit=${
                lastFetchedId || !includeNormalized ? SIZE_PACKS_LIMIT_WITH_PAGINATION : SIZE_PACKS_LIMIT
            }${includeNormalized ? "&includeNormalizedSizes=true" : ""}${
                lastFetchedId ? `&__last_id=${lastFetchedId}` : ""
            }`;
            return (
                ajax({
                    method: "GET",
                    url: `${siteConfig.api}/api/v3.0/items/packs?${queryString}`,
                })
                    .flatMap(({ response }) => {
                        if (response.length === 0) {
                            fullyFetchedBrands.push(brand);
                            return [sizePacksFetchComplete()];
                        }
                        return [
                            sizePacksFetchComplete(
                                response.reduce(
                                    (
                                        acc,
                                        {
                                            _id: id,
                                            name,
                                            brand,
                                            brand_identifier: brandIdentifier,
                                            pack_type: packType,
                                            pack_units,
                                            active,
                                            upc,
                                        },
                                    ) => {
                                        if (!acc[brand]) {
                                            acc[brand] = {};
                                        }
                                        acc[brand][id] = {
                                            id,
                                            name,
                                            brand,
                                            brandIdentifier,
                                            packType,
                                            packUnits: pack_units.map(({ _id: id, key, normalized_key, ...rest }) => ({
                                                id,
                                                normalized_key: normalized_key ? normalized_key : key,
                                                ...rest,
                                            })),
                                            packUPC: upc,
                                            active,
                                        };

                                        return acc;
                                    },
                                    { [brand]: sizePacks[brand] ? { ...sizePacks[brand] } : {} },
                                ),
                            ),
                        ];
                    })
                    .catch((error) => {
                        if (error.status >= 500) {
                            return Observable.of(sizePacksFetchComplete({ [brand]: {} }, true));
                        }
                        return Observable.of(sizePacksFetchComplete(undefined, true));
                    }) ?? []
            );
        });
};
